import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchDemoCatalog,
  fetchDemoSchools,
  enableDemoSchool,
  disableDemoSchool
} from './demoApi';
import { getFallbackDemoCatalog } from './demoCatalog';
import { setActiveSchool } from '../utils/activeSchool';
import { formatDate } from '../utils/dateFormat';

export default function DemoSchoolSettings() {
  const { t, i18n } = useTranslation(['ownerMobile', 'common']);
  const [connected, setConnected] = useState([]);
  const [catalog, setCatalog] = useState(() => getFallbackDemoCatalog());
  const [catalogFromApi, setCatalogFromApi] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pickSport, setPickSport] = useState(false);
  const [loadError, setLoadError] = useState('');

  async function load() {
    setLoading(true);
    setLoadError('');
    try {
      const [list, cat] = await Promise.all([fetchDemoSchools(), fetchDemoCatalog()]);
      setConnected(Array.isArray(list) ? list : []);
      if (Array.isArray(cat) && cat.length > 0) {
        setCatalog(cat);
        setCatalogFromApi(true);
      } else {
        setCatalog(getFallbackDemoCatalog());
        setCatalogFromApi(false);
        setLoadError(t('ownerMobile:demo.catalogFallback'));
      }
    } catch (err) {
      setConnected([]);
      setCatalog(getFallbackDemoCatalog());
      setCatalogFromApi(false);
      setLoadError(err?.message || t('common:error'));
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, [i18n.language]);

  async function handleEnable(sportTypeId) {
    setBusy(true);
    try {
      const data = await enableDemoSchool(sportTypeId);
      await load();
      setPickSport(false);
      setActiveSchool({
        schoolId: data.demoSchoolId,
        schoolName: data.schoolName,
        accessRole: 'demo',
        isDemoSchool: true
      });
      window.location.href = '/school';
    } catch (err) {
      alert(err?.message || t('ownerMobile:demo.connectFailed'));
    }
    setBusy(false);
  }

  async function handleDisable() {
    setBusy(true);
    try {
      await disableDemoSchool();
      await load();
    } catch (err) {
      alert(err?.message || t('ownerMobile:demo.disableFailed'));
    }
    setBusy(false);
  }


  return (
    <div id="demo-schools" style={{
      background: '#fff', borderRadius: 12, padding: '16px 18px',
      border: '1px solid var(--sp-gray-100)', marginBottom: 20
    }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
        {t('ownerMobile:demo.sectionTitle')}
      </div>

      {loading ? (
        <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>{t('common:loading')}</p>
      ) : (
        <>
          {loadError && !catalogFromApi ? (
            <p style={{ fontSize: 13, color: '#b45309', background: '#fffbeb', padding: '10px 12px', borderRadius: 8, margin: '0 0 12px' }}>
              {loadError}
            </p>
          ) : null}

          {connected.length === 0 ? (
            <>
              <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 12px' }}>
                {t('ownerMobile:demo.emptyHint')}
              </p>
              {!pickSport ? (
                <button type="button" onClick={() => setPickSport(true)} style={btnPrimary} disabled={busy}>
                  {t('ownerMobile:demo.connectBtn')}
                </button>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {catalog.map((c) => (
                    <button
                      key={c.sportTypeId}
                      type="button"
                      onClick={() => handleEnable(c.sportTypeId)}
                      style={chipBtn}
                      disabled={busy}
                    >
                      {c.sportEmoji} {c.sport || c.name}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            connected.map((d) => (
              <div key={d.id} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {d.sportEmoji} {d.name} · {d.sport}
                </div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                  {t('ownerMobile:demo.connectedAt', {
                    date: formatDate(d.enabledAt)
                  })}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveSchool({ schoolId: d.id, schoolName: d.name, accessRole: 'demo', isDemoSchool: true });
                      window.location.href = '/school';
                    }}
                    style={btnSecondary}
                  >
                    {t('ownerMobile:demo.open')}
                  </button>
                  <button type="button" onClick={handleDisable} style={btnDanger} disabled={busy}>
                    {t('ownerMobile:demo.disable')}
                  </button>
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}

const btnPrimary = {
  padding: '10px 16px', background: '#EB3333', color: '#fff',
  border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600
};
const btnSecondary = {
  padding: '8px 14px', background: '#f3eeff', color: '#EB3333',
  border: '1px solid #c5cae9', borderRadius: 8, cursor: 'pointer', fontWeight: 600
};
const btnDanger = {
  padding: '8px 14px', background: '#fff', color: '#c62828',
  border: '1px solid #ffcdd2', borderRadius: 8, cursor: 'pointer'
};
const chipBtn = {
  padding: '8px 12px', background: '#f5f7fb', border: '1px solid #ddd',
  borderRadius: 999, cursor: 'pointer', fontSize: 13
};
